/**
 * useKanbanCardTags — the open card's tags, reconciled against the server
 * (SE-6920).
 *
 * ## The bug this exists for
 *
 * The preview drawer's TAGS section was derived entirely from the board read
 * (`ListKanbanCards`, `cache-first` — `useKanbanBoardQueries`). Nothing
 * invalidates that cache when the SERVER changes a card's tags, which is
 * exactly what an automation rule does (SE-6860). So a tag applied server-side
 * stayed invisible until a full reload, while the SAME drawer's Activity
 * section — which runs its own query on open — already reported it. Two
 * sections of one drawer contradicting each other; the board is measurably
 * right and the client cache is stale.
 *
 * This hook adds the missing reconciliation at CARD scope: one small read per
 * open, rather than invalidating the board query whose complexity SE-5063 caps.
 *
 * ## Why the read is `no-cache` — SE-7698
 *
 * It was `cache-and-network`, and that made the reconciliation UNSOUND rather
 * than merely cached. `KanbanCardTags` and `ListKanbanCards` both select
 * `tags` on `KanbanCard`, so both writes land in ONE normalised slot —
 * `KanbanCard:<id>.tags`. A `useQuery` result is a view of that slot, not of
 * the response the query received. So the answer this hook believed it was
 * holding was really "whatever wrote the slot last", and a board list read
 * landing a quarter of a second later silently reverted the drawer to the very
 * copy this hook exists to override.
 *
 * Measured, not inferred — CI run 33250735876, shard 3, from the failing
 * attempt's own trace:
 *
 * | t (mm:ss.mmm) | write                                       | slot becomes |
 * | ------------- | ------------------------------------------- | ------------ |
 * | 26.243        | `KanbanCardTags` answers with the card's tag | `[tag]`      |
 * | 26.519        | `ListKanbanCards` answers with the board copy | `[]`        |
 *
 * The section then rendered `No tags.` for a card whose card-scoped read had
 * just reported one, which is precisely the SE-6920 symptom re-created by the
 * fix for it. Whether it happened at all came down to which of two responses
 * the network returned first, so it presented as a flaky e2e (four attempts
 * red in one nightly, one attempt red then green in the next).
 *
 * `no-cache` gives this hook its OWN answer: the response is not written to the
 * cache and the result is not read back out of it, so no board read can
 * downgrade it. The card-scoped read is a reconciliation probe against the
 * board's copy — sharing a cache slot with the thing it is correcting is
 * self-defeating.
 *
 * The cost is the one this hook can afford: the board's cached card no longer
 * picks up the fresh tags as a side effect. That was a documented convenience,
 * never SE-6920's acceptance criterion, and the board surface keeps refreshing
 * its own copy exactly as it did before this hook existed.
 *
 * ## Fallback, not replacement
 *
 * The board's tags remain the initial value. Until the card-scoped read
 * answers — and whenever it cannot — the drawer renders what the board already
 * had, so opening a drawer never flashes an empty tag row on its way to the
 * same answer. `No tags.` is therefore shown only when the fresh response
 * CONFIRMS an empty list, or when there was no fallback to begin with.
 *
 * A null `kanbanCard` (dead id, or a card the viewer may not read) is treated
 * as "no fresh answer" for the same reason: the board's tags are still the best
 * thing known about the card, and a drawer must not empty a populated section
 * because a secondary read came back nullish.
 *
 * ## …and the board copy takes the section back when it MOVES
 *
 * `no-cache` costs one thing that has to be given back. SE-6859's add-tag
 * affordance edits tags through the board's mutations, which write the
 * normalised card — so the edit reaches this hook as a change to `boardTags`,
 * and an answer frozen at drawer-open time would hide the tag the user just
 * added until they closed and reopened the drawer.
 *
 * So the fresh answer holds the section only while the board copy is
 * UNCHANGED since that answer arrived. The moment the board copy moves — a tag
 * edit, or a board read that genuinely learned something new — it is the newer
 * of the two and it wins. What it can no longer do is win by simply repeating
 * itself, which is the entire failure above: the board copy in that trace was
 * `[]` before the drawer opened and `[]` after, and it took the section anyway.
 * @module features/player-kanban/hooks/useKanbanCardTags
 */

import { useMemo, useState } from "react";

import { useKanbanCardTagsQuery } from "@/generated/graphql";

/** A tag chip: everything the drawer's TAGS section renders. */
export interface KanbanCardTag {
  /** Tag id. */
  readonly id: string;
  /** Tag display name. */
  readonly name: string;
}

/** Arguments for {@link useKanbanCardTags}. */
export interface UseKanbanCardTagsArgs {
  /** The card to reconcile. Absent while the board resolves — skips the read. */
  readonly kanbanCardId?: string | null;
  /** The board the card sits on; scopes each tag's colour. Required by the
   * query, so a nullish id skips the read. */
  readonly kanbanBoardId?: string | null;
  /** The tags the board read already produced — the fallback, and the initial
   * value. */
  readonly boardTags: readonly KanbanCardTag[];
  /** Skip the read (e.g. while the surface is closed). */
  readonly skip?: boolean;
}

/**
 * The card-scoped answer, paired with the board copy it was taken against.
 * @remarks
 * The pairing is the point: it is what lets a later render tell "the board
 * copy MOVED" apart from "the board copy was written again with the same
 * contents", which a bare comparison against the current copy cannot do.
 */
interface CardScopedAnswer {
  /** The card the answer describes; a different card invalidates it. */
  readonly cardId: string;
  /** The tags the card-scoped read returned. */
  readonly tags: readonly KanbanCardTag[];
  /** The board copy as it stood when this answer arrived. */
  readonly boardTagsWhenAnswered: readonly KanbanCardTag[];
}

/**
 * Whether two tag lists carry the same tags in the same order.
 * @remarks
 * Compared by CONTENT, never by reference: `boardTags` is derived per render by
 * the drawer's parent, so a reference test would report "the board moved" on
 * every render and hand the section straight back to the copy this hook exists
 * to override.
 * @param a - One tag list.
 * @param b - The other tag list.
 * @returns True when the two lists are equivalent.
 */
const sameTags = (
  a: readonly KanbanCardTag[],
  b: readonly KanbanCardTag[]
): boolean =>
  a.length === b.length &&
  a.every(
    (tag, index) => tag.id === b[index]?.id && tag.name === b[index]?.name
  );

/**
 * Resolve the tags to render for one kanban card.
 * @param args - {@link UseKanbanCardTagsArgs}
 * @param args.kanbanCardId - The card to reconcile; nullish skips the read
 * @param args.kanbanBoardId - The board scoping tag colours; nullish skips
 * @param args.boardTags - The board read's tags, used as the fallback
 * @param args.skip - Skip the read while the surface is closed
 * @returns The card-scoped answer while it is the newest thing known about the
 *   card, else the board's tags.
 */
export const useKanbanCardTags = ({
  kanbanCardId,
  kanbanBoardId,
  boardTags,
  skip = false,
}: UseKanbanCardTagsArgs): readonly KanbanCardTag[] => {
  // Data-readiness, never drawer visibility: both ids are required variables,
  // and the caller owns the "is this surface live" half through `skip`.
  const isSkipped = skip || !kanbanCardId || !kanbanBoardId;

  const { data } = useKanbanCardTagsQuery({
    variables: { id: kanbanCardId ?? "", kanbanBoardId: kanbanBoardId ?? "" },
    // Load-bearing — see the `no-cache` section of the module docblock. This
    // read must not share `KanbanCard:<id>.tags` with the board list read it
    // exists to correct.
    fetchPolicy: "no-cache",
    skip: isSkipped,
  });

  // `undefined` covers every "no fresh answer" case at once — skipped, in
  // flight, errored, or `kanbanCard: null` — and each of them falls back.
  // An EMPTY array is a fresh answer and must win over a stale populated one.
  const freshTags = useMemo(
    () => data?.kanbanCard?.tags?.map(tag => ({ id: tag.id, name: tag.name })),
    [data?.kanbanCard?.tags]
  );

  // Adjusting state during render — React's documented alternative to a reset
  // effect, and the pattern this feature already uses (see the tag-selector
  // reset in `PlayerPreviewDrawerContainer`). Every branch converges: each one
  // is false on the very next render, and a changing `boardTags` enters none of
  // them, which is exactly what makes the board copy's movement observable
  // rather than self-erasing.
  const [answer, setAnswer] = useState<CardScopedAnswer | null>(null);
  const answersThisCard = answer !== null && answer.cardId === kanbanCardId;
  if (answer !== null && !answersThisCard) {
    // A different card is open. Its predecessor's tags describe nobody now, so
    // they are dropped rather than re-labelled onto the new card.
    setAnswer(null);
  } else if (
    freshTags !== undefined &&
    kanbanCardId &&
    // Compared by CONTENT. A reference test here is not a stricter version of
    // this — it is a live render loop, because a query result that is freshly
    // allocated on each render then re-enters this branch forever. Measured,
    // not guessed: `useKanbanCardTags.resultIdentity.test.tsx` drives exactly
    // that input and the reference form dies on React's re-render limit.
    (!answersThisCard || !sameTags(freshTags, answer.tags))
  ) {
    setAnswer({
      cardId: kanbanCardId,
      tags: freshTags,
      boardTagsWhenAnswered: boardTags,
    });
  } else if (freshTags === undefined && answer !== null) {
    // The read went away (the surface closed, or the ids went nullish). The
    // board copy is the best thing known about the card again.
    setAnswer(null);
  }

  // The board copy has MOVED since the answer was taken, so it is the newer of
  // the two — a tag edit (SE-6859), or a board read that learned something.
  const boardMovedOn =
    answer !== null && !sameTags(boardTags, answer.boardTagsWhenAnswered);

  return useMemo(
    () => (answer !== null && !boardMovedOn ? answer.tags : boardTags),
    [answer, boardMovedOn, boardTags]
  );
};
